import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Save, Plus, X, Sparkles } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const BackgroundEditor = () => {
  const [settings, setSettings] = useState({
    gradient_colors: ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#00f2fe'],
    animation_enabled: true,
    animation_speed: 15,
    overlay_enabled: true
  });
  const [loading, setLoading] = useState(false);
  const [newColor, setNewColor] = useState('#667eea');
  const { toast } = useToast();

  const presetGradients = [
    {
      name: 'Purple Dream',
      colors: ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#00f2fe']
    },
    {
      name: 'Sunset',
      colors: ['#ff6b6b', '#ffa07a', '#ff8c00', '#ffd700', '#ffb6c1']
    },
    {
      name: 'Ocean Blue',
      colors: ['#0093E9', '#80D0C7', '#13547a', '#3a7bd5', '#00d2ff']
    },
    {
      name: 'Forest Green',
      colors: ['#134E5E', '#71B280', '#38ef7d', '#11998e', '#06beb6']
    },
    {
      name: 'Pink Paradise',
      colors: ['#f857a6', '#ff5858', '#ff9a9e', '#fecfef', '#fda085']
    },
    {
      name: 'Dark Mode',
      colors: ['#0f0c29', '#302b63', '#24243e', '#1a1a2e', '#16213e']
    },
    {
      name: 'Neon Lights',
      colors: ['#08ffc8', '#00ff88', '#00ff00', '#39ff14', '#0ff0fc']
    }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/background`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch background settings:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/background`, settings);
      toast({
        title: 'Success',
        description: 'Background settings updated successfully'
      });
      // Reload page to see changes
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error('Save failed:', error);
      toast({
        title: 'Error',
        description: 'Failed to save background settings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const addColor = () => {
    if (newColor && !settings.gradient_colors.includes(newColor)) {
      setSettings({
        ...settings,
        gradient_colors: [...settings.gradient_colors, newColor]
      });
      setNewColor('#667eea');
    }
  };

  const removeColor = (index) => {
    if (settings.gradient_colors.length > 2) {
      setSettings({
        ...settings,
        gradient_colors: settings.gradient_colors.filter((_, i) => i !== index)
      });
    } else {
      toast({
        title: 'Warning',
        description: 'You need at least 2 colors for a gradient',
        variant: 'destructive'
      });
    }
  };

  const applyPreset = (preset) => {
    setSettings({
      ...settings,
      gradient_colors: preset.colors
    });
    toast({
      title: 'Preset Applied',
      description: `${preset.name} gradient has been applied`
    });
  };

  const gradientPreview = `linear-gradient(135deg, ${settings.gradient_colors.join(', ')})`;

  return (
    <Card className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Sparkles className="w-6 h-6 text-pink-500" />
          <h2 className="text-2xl font-bold">Background Settings</h2>
        </div>
        <Button onClick={handleSave} disabled={loading} className="bg-pink-500 hover:bg-pink-600">
          <Save className="w-4 h-4 mr-2" />
          Save Changes
        </Button>
      </div>

      <div className="space-y-8">
        {/* Live Preview */}
        <div>
          <Label className="mb-2">Live Preview</Label>
          <div 
            className="w-full h-64 rounded-2xl shadow-lg relative overflow-hidden"
            style={{ 
              background: gradientPreview,
              backgroundSize: '400% 400%',
              animation: settings.animation_enabled ? `gradientShift ${settings.animation_speed}s ease infinite` : 'none'
            }}
          >
            {settings.overlay_enabled && (
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            )}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-white/90 backdrop-blur-sm rounded-xl p-6 text-center">
                <h3 className="text-2xl font-bold mb-2">Your Website</h3>
                <p className="text-gray-600">This is how your background will look</p>
              </div>
            </div>
          </div>
        </div>

        {/* Gradient Colors */}
        <div>
          <Label className="mb-4">Gradient Colors</Label>
          <div className="grid grid-cols-5 gap-4 mb-4">
            {settings.gradient_colors.map((color, index) => (
              <div key={index} className="relative group">
                <div 
                  className="w-full h-20 rounded-lg shadow-md cursor-pointer hover:scale-105 transition-transform"
                  style={{ backgroundColor: color }}
                ></div>
                <Input
                  type="color"
                  value={color}
                  onChange={(e) => {
                    const newColors = [...settings.gradient_colors];
                    newColors[index] = e.target.value;
                    setSettings({ ...settings, gradient_colors: newColors });
                  }}
                  className="mt-2"
                />
                <Button
                  size="sm"
                  variant="destructive"
                  className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => removeColor(index)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
          
          <div className="flex items-center space-x-2">
            <Input
              type="color"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              className="w-20"
            />
            <Input
              type="text"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              placeholder="#667eea"
              className="flex-1"
            />
            <Button onClick={addColor} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Add Color
            </Button>
          </div>
        </div>

        {/* Preset Gradients */}
        <div>
          <Label className="mb-4">Preset Gradients</Label>
          <div className="grid grid-cols-4 gap-4">
            {presetGradients.map((preset, index) => (
              <button
                key={index}
                onClick={() => applyPreset(preset)}
                className="relative group"
              >
                <div 
                  className="w-full h-24 rounded-xl shadow-md hover:shadow-xl transition-all hover:scale-105"
                  style={{ background: `linear-gradient(135deg, ${preset.colors.join(', ')})` }}
                ></div>
                <div className="absolute inset-0 flex items-end justify-center pb-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded-xl">
                  <span className="text-white text-sm font-semibold">{preset.name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Animation Settings */}
        <div className="grid grid-cols-2 gap-6">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <Label>Animation Enabled</Label>
              <p className="text-xs text-gray-500">Animate gradient movement</p>
            </div>
            <Switch
              checked={settings.animation_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, animation_enabled: checked })}
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <Label>Overlay Effect</Label>
              <p className="text-xs text-gray-500">Add subtle overlay</p>
            </div>
            <Switch
              checked={settings.overlay_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, overlay_enabled: checked })}
            />
          </div>
        </div>

        {/* Animation Speed */}
        {settings.animation_enabled && (
          <div>
            <Label>Animation Speed: {settings.animation_speed}s</Label>
            <input
              type="range"
              min="5"
              max="30"
              value={settings.animation_speed}
              onChange={(e) => setSettings({ ...settings, animation_speed: parseInt(e.target.value) })}
              className="w-full mt-2"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Fast (5s)</span>
              <span>Slow (30s)</span>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default BackgroundEditor;
